"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useCallback, useEffect, useId, useState } from "react";
import { Loader2, Settings2, X } from "lucide-react";
import { useDictation, type LockedDictationTarget } from "@/hooks/use-dictation";
import type { DictationMode } from "@/lib/dictation/settings";
import { cn } from "@/lib/utils";
import "./dictation-control.css";
interface VoiceOrbProps {
  mode: DictationMode;
  label: string;
  lockTarget: () => LockedDictationTarget | null;
  isUnavailable?: boolean;
}

/** Click-only voice control; destination and mode belong to the activation. */
export function VoiceOrb(t0) {
  const $ = _c(62);
  const {
    mode,
    label,
    lockTarget,
    isUnavailable: t1
  } = t0;
  const isUnavailable = t1 === undefined ? false : t1;
  const [showDetails, setShowDetails] = useState(false);
  const descriptionId = useId();
  let t2;
  if ($[0] !== isUnavailable) {
    t2 = () => !isUnavailable;
    $[0] = isUnavailable;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isEligible = t2;
  let t3;
  if ($[2] !== isEligible || $[3] !== isUnavailable || $[4] !== lockTarget || $[5] !== mode) {
    t3 = {
      mode,
      lockTarget,
      isEligible,
      isUnavailable,
      enableShortcuts: false
    };
    $[2] = isEligible;
    $[3] = isUnavailable;
    $[4] = lockTarget;
    $[5] = mode;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const voice = useDictation(t3);
  const {
    status,
    error,
    feedback,
    cancel
  } = voice;
  const isBusy = status === "listening" || status === "processing";
  let t4;
  let t5;
  if ($[7] !== cancel || $[8] !== isUnavailable) {
    t4 = () => {
      if (isUnavailable) {
        cancel();
      }
    };
    t5 = [isUnavailable, cancel];
    $[7] = cancel;
    $[8] = isUnavailable;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  const action = status === "listening" ? "Stop recording" : status === "processing" ? "Processing voice input" : mode === "organize" ? "Start brainstorming" : "Start dictation";
  const t6 = status === "listening";
  const t7 = status === "processing";
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = cn("dictation-float right-6 bottom-[92px]", {
      "dictation-float--listening": t6,
      "dictation-float--processing": t7
    });
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== feedback || $[15] !== label || $[16] !== showDetails || $[17] !== voice.dismissFeedback || $[18] !== voice.undoLast) {
    t9 = showDetails && <section aria-label="Voice input settings" className="dictation-float-panel dictation-float-panel--align-right dictation-float-panel--above"><div className="dictation-float-panel-header"><span>Writing into {label}</span><button type="button" aria-label="Close voice settings" onClick={() => setShowDetails(false)}><X className="size-4" /></button></div><p className="dictation-privacy">Iloris does not store audio. Audio is sent to ElevenLabs for transcription. <Link href="/privacy#voice-dictation">Privacy details</Link></p>{feedback && <div role="status" className="dictation-feedback"><span>{feedback.inserted ? "Voice text inserted." : "Voice text was not inserted."}</span>{feedback.undo && <button type="button" onClick={voice.undoLast}>Undo</button>}{feedback.copyOriginal && <button type="button" onClick={feedback.copyOriginal}>Copy original</button>}<details><summary>View original</summary><p>{feedback.original}</p></details><button type="button" aria-label="Dismiss dictation feedback" onClick={voice.dismissFeedback}><X className="size-4" /></button></div>}</section>;
    $[14] = feedback;
    $[15] = label;
    $[16] = showDetails;
    $[17] = voice.dismissFeedback;
    $[18] = voice.undoLast;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isBusy || $[21] !== status) {
    t10 = isBusy && <div role="status" className="dictation-float-status dictation-float-status--align-right">{status === "listening" ? "Listening\u2026 click again to insert" : "Processing\u2026"}</div>;
    $[20] = isBusy;
    $[21] = status;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== error || $[24] !== voice.dismissMessages) {
    t11 = error && <div role="alert" className="dictation-float-status dictation-float-status--error dictation-float-status--align-right">{error}<button type="button" aria-label="Dismiss voice error" onClick={voice.dismissMessages}><X className="size-4" /></button></div>;
    $[23] = error;
    $[24] = voice.dismissMessages;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = isUnavailable || status === "processing";
  let t13;
  if ($[26] !== status || $[27] !== voice) {
    t13 = () => status === "listening" ? voice.stopRecording() : void voice.startRecording();
    $[26] = status;
    $[27] = voice;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="dictation-orb-glow" aria-hidden={true} />;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== status) {
    t15 = status === "processing" ? <Loader2 className="size-5 animate-spin" /> : <span className="dictation-orb-stream" aria-hidden={true}><span /></span>;
    $[30] = status;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== action || $[33] !== descriptionId || $[34] !== t12 || $[35] !== t13 || $[36] !== t15) {
    t16 = <button type="button" className="dictation-mic dictation-orb" aria-label={action} aria-describedby={descriptionId} disabled={t12} onClick={t13}>{t14}{t15}</button>;
    $[32] = action;
    $[33] = descriptionId;
    $[34] = t12;
    $[35] = t13;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== showDetails) {
    t17 = () => setShowDetails(!showDetails);
    $[38] = showDetails;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Settings2 className="size-4" />;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== showDetails || $[42] !== t17) {
    t19 = <button type="button" className="dictation-panel-toggle" aria-label="Voice settings" aria-expanded={showDetails} onClick={t17}>{t18}</button>;
    $[41] = showDetails;
    $[42] = t17;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t16 || $[45] !== t19) {
    t20 = <div className="dictation-float-orb-wrap">{t16}{t19}</div>;
    $[44] = t16;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <span className="block rounded-full bg-popover px-2 py-1 text-center text-xs text-popover-foreground">Click here</span>;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== cancel || $[49] !== isBusy) {
    t22 = isBusy && <button type="button" onClick={cancel} className="dictation-float-cancel">Cancel</button>;
    $[48] = cancel;
    $[49] = isBusy;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  const t23 = isUnavailable ? "This writing field is unavailable." : "Press to start listening, then press again to stop and insert. Escape cancels.";
  let t24;
  if ($[51] !== descriptionId || $[52] !== t23) {
    t24 = <span id={descriptionId} className="sr-only">{t23}</span>;
    $[51] = descriptionId;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t10 || $[55] !== t11 || $[56] !== t20 || $[57] !== t22 || $[58] !== t24 || $[59] !== t8 || $[60] !== t9) {
    t25 = <div data-testid="dictation-float" className={t8}>{t9}{t10}{t11}{t20}{t21}{t22}{t24}</div>;
    $[54] = t10;
    $[55] = t11;
    $[56] = t20;
    $[57] = t22;
    $[58] = t24;
    $[59] = t8;
    $[60] = t9;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  return t25;
}